import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from "react";

type QuickMode = "all" | "credits" | "debits";

const applyModel = (params: any, min: string, max: string, mode: QuickMode) => {
  let model: any = null;

  if (mode === "credits") {
    model = { filterType: "number", type: "greaterThan", filter: 0 };
  } else if (mode === "debits") {
    model = { filterType: "number", type: "lessThan", filter: 0 };
  } else if (min !== "" && max !== "") {
    model = {
      filterType: "number",
      type: "inRange",
      filter: parseFloat(min),
      filterTo: parseFloat(max),
    };
  } else if (min !== "") {
    model = {
      filterType: "number",
      type: "greaterThanOrEqual",
      filter: parseFloat(min),
    };
  } else if (max !== "") {
    model = {
      filterType: "number",
      type: "lessThanOrEqual",
      filter: parseFloat(max),
    };
  }

  params.parentFilterInstance((inst: any) => {
    inst.setModel(model);
    params.api.onFilterChanged();
  });
};

const btnStyle = (active: boolean): React.CSSProperties => ({
  fontSize: 10,
  padding: "1px 4px",
  height: 20,
  border: "1px solid",
  borderColor: active ? "#0d6efd" : "#ccc",
  borderRadius: 3,
  background: active ? "#0d6efd" : "transparent",
  color: active ? "#fff" : "#555",
  cursor: "pointer",
  flexShrink: 0,
  lineHeight: 1,
});

export const AmountRangeFloatingFilter = forwardRef<any, any>((props, ref) => {
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [mode, setMode] = useState<QuickMode>("all");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({
    onParentModelChanged(model: any) {
      if (!model) {
        setMin("");
        setMax("");
        setMode("all");
        return;
      }
      if (model.type === "inRange") {
        setMode("all");
        setMin(model.filter != null ? String(model.filter) : "");
        setMax(model.filterTo != null ? String(model.filterTo) : "");
      } else if (model.type === "greaterThan" && model.filter === 0) {
        setMode("credits");
        setMin("");
        setMax("");
      } else if (model.type === "lessThan" && model.filter === 0) {
        setMode("debits");
        setMin("");
        setMax("");
      } else {
        setMode("all");
        setMin(model.filter != null ? String(model.filter) : "");
        setMax(model.filterTo != null ? String(model.filterTo) : "");
      }
    },
  }));

  const handleMin = useCallback(
    (v: string) => {
      setMin(v);
      setMode("all");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        applyModel(props, v, max, "all");
      }, 500);
    },
    [props, max]
  );

  const handleMax = useCallback(
    (v: string) => {
      setMax(v);
      setMode("all");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        applyModel(props, min, v, "all");
      }, 500);
    },
    [props, min]
  );

  const handleMode = useCallback(
    (m: QuickMode) => {
      const next = mode === m ? "all" : m;
      setMode(next);
      setMin("");
      setMax("");
      applyModel(props, "", "", next);
    },
    [props, mode]
  );

  const inputStyle: React.CSSProperties = {
    fontSize: 11,
    padding: "1px 3px",
    height: 22,
    border: "1px solid #ccc",
    borderRadius: 3,
    width: 54,
    minWidth: 0,
  };

  return (
    <div
      className="d-flex align-items-center"
      style={{ gap: 2, padding: "0 2px", height: "100%" }}
    >
      <button style={btnStyle(mode === "credits")} onClick={() => handleMode("credits")}>
        Cr
      </button>
      <button style={btnStyle(mode === "debits")} onClick={() => handleMode("debits")}>
        Dr
      </button>
      <input
        type="number"
        placeholder="Min"
        value={min}
        onChange={(e) => handleMin(e.target.value)}
        style={inputStyle}
        disabled={mode !== "all"}
      />
      <span style={{ fontSize: 10, color: "#888", flexShrink: 0 }}>–</span>
      <input
        type="number"
        placeholder="Max"
        value={max}
        onChange={(e) => handleMax(e.target.value)}
        style={inputStyle}
        disabled={mode !== "all"}
      />
    </div>
  );
});

AmountRangeFloatingFilter.displayName = "AmountRangeFloatingFilter";
